import {
  privateGetApi,
  privatePostApi,
  privatePutApi,
  privateDeleteApi,
} from "../../utils/apiHelper";
import {
  setOrganizations,
  setActiveOrganization,
  updateOrganizationInList,
} from "../slices/organizationSlice";
import { normalizeId } from "../../utils/organizationId";

function normalizeUserForStorage(user) {
  if (!user) return user;
  return {
    ...user,
    id: normalizeId(user.id),
    activeOrganizationId: normalizeId(user.activeOrganizationId),
  };
}

function normalizeOrganizationsForStorage(organizations) {
  if (!Array.isArray(organizations)) return organizations;
  return organizations.map((org) => ({
    ...org,
    id: normalizeId(org.id),
  }));
}

function persistSession(result) {
  if (result.accessToken) {
    localStorage.setItem("accessToken", result.accessToken);
  }
  if (result.user) {
    localStorage.setItem(
      "user",
      JSON.stringify(normalizeUserForStorage(result.user)),
    );
  }
  if (result.emailSettings) {
    localStorage.setItem("emailSettings", JSON.stringify(result.emailSettings));
  }
  if (result.organizations) {
    const organizations = normalizeOrganizationsForStorage(
      result.organizations,
    );
    localStorage.setItem("organizations", JSON.stringify(organizations));
  }
  const currentTime = new Date().getTime();
  const expireIn = result.expireIn || 8;
  localStorage.setItem(
    "expireAt",
    JSON.stringify(currentTime + 60000 * 60 * expireIn),
  );
}

export const fetchOrganizations = () => (dispatch) => {
  return privateGetApi("/organizations").then((response) => {
    if (response.status === 200) {
      const organizations = normalizeOrganizationsForStorage(
        response.data.result.organizations || [],
      );
      dispatch(setOrganizations(organizations));
      localStorage.setItem("organizations", JSON.stringify(organizations));
      return organizations;
    }
    return [];
  });
};

export const createOrganization = (name) => (dispatch) => {
  return new Promise((resolve, reject) => {
    privatePostApi("/organizations", { name: String(name || "").trim() })
      .then((response) => {
        if (response.status === 200) {
          const result = response.data.result;
          persistSession(result);
          const organizations = normalizeOrganizationsForStorage(
            result.organizations || [],
          );
          dispatch(setOrganizations(organizations));
          dispatch(
            setActiveOrganization({
              id: normalizeId(result.user?.activeOrganizationId),
              role: result.user?.role,
            }),
          );
          resolve(result);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};

export const switchOrganization = (organizationId) => (dispatch) => {
  const orgId = normalizeId(organizationId);
  return new Promise((resolve, reject) => {
    privatePostApi("/organizations/switch", { organizationId: orgId })
      .then((response) => {
        if (response.status === 200) {
          const result = response.data.result;
          persistSession(result);
          const organizations = normalizeOrganizationsForStorage(
            result.organizations || [],
          );
          dispatch(setOrganizations(organizations));
          dispatch(
            setActiveOrganization({
              id: normalizeId(result.user?.activeOrganizationId),
              role: result.user?.role,
            }),
          );
          resolve(result);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};

export const updateOrganization = (organizationId, payload) => () => {
  return new Promise((resolve, reject) => {
    privatePutApi(`/organizations/${normalizeId(organizationId)}`, payload)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data.result);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};

export const fetchOrganizationDetail = (organizationId) => () => {
  return new Promise((resolve, reject) => {
    privateGetApi(`/organizations/${normalizeId(organizationId)}`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data.result.organization);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};

// PRD item 3 (FR-3.11): fiduciary details used as the fallback identity on
// every form's DPDP notice -- see PUT /organizations/:id/dpdp.
export const updateDpdpSettings = (organizationId, payload) => () => {
  return new Promise((resolve, reject) => {
    privatePutApi(`/organizations/${normalizeId(organizationId)}/dpdp`, payload)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data.result);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};

export const updateContactsTableColumns =
  (organizationId, contactsTableColumns) => () => {
    return new Promise((resolve, reject) => {
      privatePutApi(
        `/organizations/${normalizeId(organizationId)}/contacts-table-columns`,
        { contactsTableColumns },
      )
        .then((response) => {
          if (response.status === 200) {
            resolve(response.data.result);
          } else {
            reject(response.message);
          }
        })
        .catch(reject);
    });
  };

export const saveContactsTableColumns =
  (organizationId, contactsTableColumns) => (dispatch) => {
    return dispatch(
      updateContactsTableColumns(organizationId, contactsTableColumns),
    ).then((result) => {
      const changes = {
        contactsTableColumns: result?.organization?.contactsTableColumns ??
          contactsTableColumns,
      };
      dispatch(updateOrganizationInList({ id: organizationId, changes }));

      const organizations = JSON.parse(
        localStorage.getItem("organizations") || "[]",
      );
      const updatedOrganizations = organizations.map((orgItem) =>
        String(orgItem.id) === String(organizationId)
          ? { ...orgItem, ...changes }
          : orgItem,
      );
      localStorage.setItem(
        "organizations",
        JSON.stringify(updatedOrganizations),
      );

      return result;
    });
  };

export const fetchMembers = (organizationId) => {
  return privateGetApi(`/organizations/${normalizeId(organizationId)}/members`);
};

export const inviteMember = (organizationId, payload) => {
  return privatePostApi(
    `/organizations/${normalizeId(organizationId)}/invites`,
    payload,
  );
};

export const updateMemberRole = (organizationId, userId, role) => {
  return privatePutApi(
    `/organizations/${normalizeId(organizationId)}/members/${normalizeId(userId)}`,
    {
      role,
    },
  );
};

export const removeMember = (organizationId, userId) => {
  return privateDeleteApi(
    `/organizations/${normalizeId(organizationId)}/members/${normalizeId(userId)}`,
  );
};

export const acceptInvite = (token) => (dispatch) => {
  return new Promise((resolve, reject) => {
    privatePostApi(`/invites/${token}/accept`)
      .then((response) => {
        if (response.status === 200) {
          const result = response.data.result;
          persistSession(result);
          const organizations = normalizeOrganizationsForStorage(
            result.organizations || [],
          );
          dispatch(setOrganizations(organizations));
          dispatch(
            setActiveOrganization({
              id: normalizeId(result.user?.activeOrganizationId),
              role: result.user?.role,
            }),
          );
          resolve(result);
        } else {
          reject(response.message);
        }
      })
      .catch(reject);
  });
};
